/**
 * FIN module export utilities for CSV and Excel exports
 */

export interface ColumnDefinition {
  key: string;
  label: string;
}

function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

function formatExportValue(value: any): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    } catch { /* fall through */ }
  }

  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toLocaleString('en-US');
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function prepareData(
  data: Record<string, any>[],
  columns?: ColumnDefinition[],
): { cols: ColumnDefinition[]; rows: Record<string, string>[] } {
  if (data.length === 0) return { cols: [], rows: [] };
  const cols = columns ?? Object.keys(data[0]).map(key => ({ key, label: key }));
  const rows = data.map(row => {
    const formatted: Record<string, string> = {};
    for (const col of cols) {
      formatted[col.key] = formatExportValue(row[col.key]);
    }
    return formatted;
  });
  return { cols, rows };
}

export function exportToCSV(
  data: Record<string, any>[],
  filename: string,
  columns?: ColumnDefinition[],
): void {
  if (data.length === 0) return;

  const cols = columns ?? Object.keys(data[0]).map(key => ({ key, label: key }));
  const headerRow = cols.map(c => escapeCSVValue(c.label)).join(',');
  const dataRows = data.map(row =>
    cols.map(c => escapeCSVValue(row[c.key])).join(',')
  );
  const csv = [headerRow, ...dataRows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const fname = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.setAttribute('href', url);
  link.setAttribute('download', fname);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportToExcel(
  data: Record<string, any>[],
  filename: string,
  columns?: ColumnDefinition[],
): void {
  if (data.length === 0) return;

  const { cols, rows } = prepareData(data, columns);

  const headerCells = cols.map(c => `<th>${escapeHtml(c.label)}</th>`).join('');
  const bodyRows = rows.map(row => {
    const cells = cols.map(c => `<td>${escapeHtml(row[c.key])}</td>`).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8" />
      <style>
        table { border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; }
        th { background-color: #f3f4f6; font-weight: bold; }
      </style>
    </head>
    <body>
      <table>
        <thead><tr>${headerCells}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </body>
    </html>
  `.trim();

  const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const fname = filename.endsWith('.xls') ? filename : `${filename}.xls`;
  link.setAttribute('href', url);
  link.setAttribute('download', fname);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
